function valueOf(resource) {
    return resource?.data || resource || {}
}

function firstPresent(...values) {
    return values.find(value => value !== undefined && value !== null && value !== '')
}

function indexValue(index, key, value) {
    if (key === null) return
    const matches = index.get(key) || []
    matches.push(value)
    index.set(key, matches)
}

function uniqueMatch(index, key) {
    if (key === null) return null
    const matches = index.get(key) || []
    return matches.length === 1 ? matches[0] : null
}

function scalarKey(value) {
    return value === undefined || value === null || value === '' ? null : String(value)
}

function pairKey(first, second) {
    const firstKey = scalarKey(first)
    const secondKey = scalarKey(second)
    return firstKey === null || secondKey === null ? null : JSON.stringify([firstKey, secondKey])
}

function targetTableId(reference) {
    const data = valueOf(reference)
    return firstPresent(data.fk_table_id, data.fk_table_p_id)
}

function targetFieldId(reference) {
    const data = valueOf(reference)
    return firstPresent(data.fk_field_id, data.fk_field_p_id)
}

/**
 * Resolves relationship targets once per render from a complete scene.
 *
 * Stable target IDs are preferred when a schema provides them. Legacy schemas
 * fall back to names, but ambiguous names deliberately resolve to null instead
 * of drawing a relationship against an arbitrary field or table.
 */
export class RelationshipResolver {
    constructor(tables = []) {
        this.tablesById = new Map()
        this.tablesByName = new Map()
        this.rowsByFieldId = new Map()
        this.rowsByTableIdAndFieldName = new Map()
        this.rowsByTableAndFieldName = new Map()

        for (const table of tables) {
            const tableData = valueOf(table)
            const tableId = firstPresent(tableData.p_id, tableData.id)
            const tableName = firstPresent(tableData.name, table.name)

            indexValue(this.tablesById, scalarKey(tableId), table)
            indexValue(this.tablesByName, scalarKey(tableName), table)

            for (const row of table.rows || []) {
                const rowData = valueOf(row)
                const fieldId = firstPresent(rowData.p_id, rowData.id)
                const fieldName = firstPresent(rowData.name, row.name)

                indexValue(this.rowsByFieldId, scalarKey(fieldId), row)
                indexValue(this.rowsByTableIdAndFieldName, pairKey(tableId, fieldName), row)
                indexValue(this.rowsByTableAndFieldName, pairKey(tableName, fieldName), row)
            }
        }
    }

    resolveTable(reference) {
        const data = valueOf(reference)
        const tableId = targetTableId(data)
        if (tableId !== undefined) {
            return uniqueMatch(this.tablesById, scalarKey(tableId))
        }

        return uniqueMatch(this.tablesByName, scalarKey(data.fk_table))
    }

    resolveRow(reference) {
        const data = valueOf(reference)
        const tableId = targetTableId(data)
        const fieldId = targetFieldId(data)

        if (fieldId !== undefined) {
            const matches = this.rowsByFieldId.get(scalarKey(fieldId)) || []
            if (tableId === undefined) return matches.length === 1 ? matches[0] : null

            const tableKey = scalarKey(tableId)
            const matchingTableRows = matches.filter(row => {
                const rowTable = valueOf(row.table)
                return scalarKey(firstPresent(rowTable.p_id, rowTable.id)) === tableKey
            })
            return matchingTableRows.length === 1 ? matchingTableRows[0] : null
        }

        if (tableId !== undefined) {
            return uniqueMatch(
                this.rowsByTableIdAndFieldName,
                pairKey(tableId, data.fk_field)
            )
        }

        return uniqueMatch(
            this.rowsByTableAndFieldName,
            pairKey(data.fk_table, data.fk_field)
        )
    }
}
